import type { LoginRequest } from '@/queries/auth/auth.schema';
import { useTranslations } from 'next-intl';
import { useFingerprintRequestIdNoCache } from '@/components/entities/analytics/components/FingerprintProvider/hooks/fingerPrintRequestId';
import { UserDeviceActivityType } from '@/components/entities/analytics/components/FingerprintProvider/types/fingerPrintType';
import { gtmUserProfileStore, pushCustomGtmEventToDataLayer } from '@/components/entities/analytics/gtm';
import Toast from '@/components/ui/Toast/toast';
import { clearUsedReferralTokenSource } from '@/libs/userSource/referralTokenSource';
import { setHadSuccessLogin } from '@/modals/modules/auth/RegisterModal/components/registrationSession.utils';
import { authApi } from '@/queries/auth/auth.api';
import { tokenManager } from '@/queries/token-manager';
import { useAppDispatch } from '@/redux/hooks';
import { checkToken } from '@/redux/slices/userSlice';

// INFO: shared email/password login flow; errors are rethrown so every caller resolves them in its own UI
export const useLogin = () => {
  const t = useTranslations('sign-in');
  const dispatch = useAppDispatch();

  const { getFingerprintRequestIdNoCache } = useFingerprintRequestIdNoCache({ activityType: UserDeviceActivityType.Login });

  const login = async (credentials: LoginRequest) => {
    const requestId = await getFingerprintRequestIdNoCache();
    const headers: Record<string, string> = {
      'x-fingerprint-request-id': requestId,
    };

    await authApi.login(credentials, headers);
    await dispatch(checkToken()).unwrap();

    setHadSuccessLogin();

    clearUsedReferralTokenSource();

    const gtmUserData = {
      id: tokenManager.getUserId() ?? undefined,
      email: credentials.username,
    };

    gtmUserProfileStore.cache(gtmUserData);
    pushCustomGtmEventToDataLayer('user_logged_in');

    Toast({
      type: 'success',
      title: t('login-successful'),
      description: t('login-successful-description'),
    });
  };

  return { login, getFingerprintRequestIdNoCache };
};
